import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Switch } from "./ui/switch";
import { RENTAL_FIELDS, CHAUFFEUR_FIELDS, TOUR_FIELDS, STATUS_OPTIONS, CLIENT_CREDENTIAL_FIELDS, YES_NO_OPTIONS, INSURED_TYPE_OPTIONS, INSURANCE_POLICY_OPTIONS, SALESPERSON_OPTIONS, TAX_RATE, computeChargesTotal, chargesHourly, hasPackage, remainingBalance, billableServiceHours, inclusiveDays, hourPeriods } from "../pages/bookingFields";
import { PackagePicker, CustomAddons } from "./booking/PackagePicker";
import { DayHoursField } from "./booking/DayHoursField";
import AffiliatePicker from "./booking/AffiliatePicker";

// Compound date + 5-minute-step time picker. Bulletproof across browsers &
// mobile — the native datetime-local input's minute wheel ignores the `step`
// attribute on many platforms. This component uses a plain date input plus a
// dropdown pre-populated with every 15-minute slot in a day.
const TIME_SLOTS = Array.from({ length: 24 * 4 }, (_, i) => {
  const h = Math.floor(i / 4);
  const m = (i % 4) * 15;
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(h)}:${pad(m)}`;
});
const timeLabel = (t) => {
  const [h, m] = t.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const hh = h % 12 === 0 ? 12 : h % 12;
  return `${hh}:${String(m).padStart(2, "0")} ${period}`;
};

function DateTime5MinInput({ value, onChange, testid }) {
  const [d, t] = (value || "").split("T");
  const displayTime = (t || "").slice(0, 5);
  // Keep an off-grid saved time (e.g. 6:50 PM from an import) selectable as-is.
  const slots = displayTime && !TIME_SLOTS.includes(displayTime) ? [...TIME_SLOTS, displayTime].sort() : TIME_SLOTS;
  const emit = (nd, nt) => {
    if (!nd) return onChange("");
    onChange(`${nd}T${nt || "00:00"}`);
  };
  return (
    <div className="flex gap-2">
      <Input
        type="date"
        value={d || ""}
        onChange={(e) => emit(e.target.value, displayTime)}
        className="bg-input border-border flex-1"
        data-testid={`${testid}-date`}
      />
      <Select value={displayTime} onValueChange={(nt) => emit(d, nt)}>
        <SelectTrigger className="bg-input border-border w-40" data-testid={`${testid}-time`}>
          <SelectValue placeholder="Select time" />
        </SelectTrigger>
        <SelectContent className="max-h-72">
          {slots.map((slot) => (
            <SelectItem key={slot} value={slot}>{timeLabel(slot)}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

// Client First/Last name inputs. First name keeps live autocomplete against existing
// clients; picking a suggestion autofills both names + phone/email/age. Both names
// compose into form.customer_name (the canonical field used everywhere else).
function ClientNameFields({ form, setForm }) {
  const [matches, setMatches] = useState([]);
  const [open, setOpen] = useState(false);

  const first = form.customer_first_name ?? ((form.customer_name || "").trim().split(/\s+/)[0] || "");
  const last = form.customer_last_name ?? ((form.customer_name || "").trim().split(/\s+/).slice(1).join(" ") || "");

  const update = (f, l) => setForm((prev) => ({
    ...prev,
    customer_first_name: f,
    customer_last_name: l,
    customer_name: `${f} ${l}`.trim(),
  }));

  useEffect(() => {
    const q = first.trim();
    if (q.length < 2) { setMatches([]); return; }
    let cancel = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/customers", { params: { search: q } });
        if (!cancel) setMatches(data.slice(0, 6));
      } catch { if (!cancel) setMatches([]); }
    }, 250);
    return () => { cancel = true; clearTimeout(t); };
  }, [first]);

  const pick = (c) => {
    setForm((prev) => ({
      ...prev,
      customer_first_name: c.first_name || "",
      customer_last_name: c.last_name || "",
      customer_name: `${c.first_name || ""} ${c.last_name || ""}`.trim(),
      customer_phone: c.phone || prev.customer_phone,
      customer_email: c.email || prev.customer_email,
      client_age: c.age || prev.client_age,
    }));
    setOpen(false);
  };

  return (
    <>
      <div className="col-span-2 md:col-span-1 relative">
        <Label className="label-mono mb-1.5 block">First Name<span className="text-primary"> *</span></Label>
        <Input
          type="text"
          autoComplete="off"
          value={first}
          onChange={(e) => { update(e.target.value, last); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          className="bg-input border-border"
          data-testid="bookings-field-customer_first_name"
        />
        {open && matches.length > 0 && (
          <div className="absolute z-50 left-0 right-0 mt-1 bg-popover border border-border rounded-md shadow-xl max-h-56 overflow-y-auto" data-testid="client-suggestions">
            {matches.map((c) => (
              <button key={c.id} type="button" onMouseDown={(e) => { e.preventDefault(); pick(c); }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-secondary/50 transition-colors" data-testid={`client-suggestion-${c.id}`}>
                <div className="font-heading">{c.first_name} {c.last_name}</div>
                <div className="text-xs text-muted-foreground">{[c.phone, c.email].filter(Boolean).join(" · ") || "—"}</div>
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="col-span-2 md:col-span-1">
        <Label className="label-mono mb-1.5 block">Last Name</Label>
        <Input
          type="text"
          autoComplete="off"
          value={last}
          onChange={(e) => update(first, e.target.value)}
          className="bg-input border-border"
          data-testid="bookings-field-customer_last_name"
        />
      </div>
    </>
  );
}

// Radio-style "bubble" selector used by the insurance fields.
function BubbleGroup({ label, value, onChange, options, required, testid }) {
  return (
    <div>
      <Label className="label-mono mb-1.5 block">{label}{required && <span className="text-primary"> *</span>}</Label>
      <div className="flex gap-2" data-testid={testid}>
        {options.map((opt) => {
          const active = value === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange(opt.value)}
              data-testid={`${testid}-${opt.value}`}
              aria-pressed={active}
              className={`px-4 py-2 rounded-full text-sm border transition-colors ${active ? "bg-primary text-primary-foreground border-primary" : "bg-input border-border text-muted-foreground hover:border-primary/60"}`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Shared booking form grid. Used by Bookings (admin dialog) and NewBooking (booking_agent page).
export function BookingForm({ formType, form, setForm, vehicles, chauffeurs, onVehicleChange, onDriverChange, excludeBookingId, hideAdminOnly = false, salespersonReadOnly = false }) {
  // Vehicle IDs already booked during the selected start/end window (greyed out).
  const [bookedIds, setBookedIds] = useState([]);
  const [checkingAvail, setCheckingAvail] = useState(false);
  const start = form.start_datetime;
  const end = form.end_datetime;
  const datesReady = Boolean(start && end && start < end);
  // SALES-023: a rental loaded WITHOUT rental_days existed before day-based
  // pricing — its total is final. Captured once, on the first render (the
  // booking is already loaded when this dialog opens), so it survives the
  // date-derivation and total-recompute effects below. The guard lives here in
  // the edit form only — never in the wizard, which always creates a NEW
  // booking that must derive.
  const legacyRentalRef = useRef(formType === "rental" && !(Number(form.rental_days) > 0));
  const [salesOtherOpen, setSalesOtherOpen] = useState(false);
  const [salesOptions, setSalesOptions] = useState(SALESPERSON_OPTIONS);
  const salesSet = new Set(salesOptions);
  const showSalesOther = salesOtherOpen || (!!form.salesperson && !salesSet.has(form.salesperson));

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/salespeople");
        const names = (data || []).filter((s) => s.active !== false).map((s) => s.name);
        if (names.length) setSalesOptions(names);
      } catch (e) { /* fall back to static list */ }
    })();
  }, []);

  useEffect(() => {
    if (!datesReady) { setBookedIds([]); return; }
    let cancelled = false;
    setCheckingAvail(true);
    (async () => {
      try {
        const params = { start, end };
        if (excludeBookingId) params.exclude_id = excludeBookingId;
        const { data } = await api.get("/vehicles/availability", { params });
        if (!cancelled) setBookedIds(data.booked_vehicle_ids || []);
      } catch {
        if (!cancelled) setBookedIds([]);
      } finally {
        if (!cancelled) setCheckingAvail(false);
      }
    })();
    return () => { cancelled = true; };
  }, [start, end, datesReady, excludeBookingId]);

  const isBooked = (id) => datesReady && bookedIds.includes(id) && id !== form.vehicle_id;

  // Auto-calc service hours from the selected start/end window (chauffeur & tour).
  // Typing directly in the field sets a manual override; clearing it re-enables auto.
  const initialWindow = useRef({ start: form.start_datetime, end: form.end_datetime, hadHours: Number(form.service_hours) > 0, standby: Number(form.standby_hours) || 0 });
  useEffect(() => {
    if (formType === "rental" || !datesReady || form.service_hours_manual) return;
    const init = initialWindow.current;
    // Editing: keep the stored hours until the times OR the standby hours move.
    if (init.hadHours && init.start === start && init.end === end
        && (Number(form.standby_hours) || 0) === init.standby) return;
    const windowHours = Math.round(((Date.parse(end) - Date.parse(start)) / 3600000) * 10) / 10;
    if (!(windowHours > 0)) return;
    const hrs = billableServiceHours(windowHours, form, formType);
    if (Number(form.service_hours) !== hrs) setForm((f) => ({ ...f, service_hours: hrs }));
  }, [start, end, datesReady, formType, form.service_hours_manual, form.standby_hours]); // eslint-disable-line react-hooks/exhaustive-deps

  // SALES-029: derive Days by the 24-hour rule, but ONLY when the dates change
  // in the form — never on open (a booking saved under the calendar rule keeps
  // its days and re-prices only if someone moves a date), never for a count the
  // agent chose (rental_days_manual), never a pre-ticket rental (legacyRentalRef).
  // The initial window is captured on first render, like the hours effect above.
  const initialRentalWindow = useRef({ start: form.start_datetime, end: form.end_datetime });
  useEffect(() => {
    if (formType !== "rental" || legacyRentalRef.current || !datesReady || form.rental_days_manual) return;
    const init = initialRentalWindow.current;
    if (init.start === start && init.end === end) return; // no recount on open
    const days = hourPeriods(start, end);
    if (days > 0 && Number(form.rental_days) !== days) setForm((f) => ({ ...f, rental_days: days }));
  }, [start, end, datesReady, formType, form.rental_days_manual]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-populate the Total field from the base charge (rental_rate OR
  // service_hours × hourly_rate) + delivery + other_charges. Any direct edit
  // to the Total input sets `total_manual=true` which suppresses further
  // recompute — clearing the field re-enables auto (same UX as service_hours).
  useEffect(() => {
    if (form.total_manual) return;
    // SALES-023: a pre-ticket rental's total is final — never recalculated, so
    // an unedited save is byte-identical. total_manual is left untouched.
    if (formType === "rental" && legacyRentalRef.current) return;
    const auto = computeChargesTotal(form, formType);
    if (auto > 0 && Number(form.total) !== auto) {
      setForm((f) => ({ ...f, total: auto }));
    }
  }, [form.rental_rate, form.service_hours, form.hourly_rate, form.delivery, form.other_charges, form.total_manual, formType, form, setForm]);

  // Remaining balance is derived, never typed: Total − deposit.
  useEffect(() => {
    if (formType === "rental") return;
    const bal = remainingBalance(form);
    if (Number(form.remaining_balance) !== bal) setForm((f) => ({ ...f, remaining_balance: bal }));
  }, [form.total, form.depo_sent, form.remaining_balance, formType, form, setForm]);

  const availableVehicles = (service) =>
    vehicles
      .filter((v) => v.service_type === service || v.service_type === "both" || !v.service_type)
      .sort((a, b) => {
        const m = (a.make || "").localeCompare(b.make || "");
        return m !== 0 ? m : (a.model || "").localeCompare(b.model || "");
      });

  const rawFields = formType === "rental" ? RENTAL_FIELDS : formType === "tour" ? TOUR_FIELDS : CHAUFFEUR_FIELDS;
  let fields = hideAdminOnly ? rawFields.filter((f) => !f.adminOnly) : rawFields;
  // SALES-023: a pre-ticket rental (no rental_days) is never recalculated, so
  // it shows NO Days field — one there would only invite an edit that does
  // nothing — and its rate keeps its original "Rate" label, not "Daily rate".
  if (formType === "rental" && legacyRentalRef.current) {
    fields = fields
      .filter((f) => f.name !== "rental_days")
      .map((f) => (f.name === "rental_rate" ? { ...f, label: "Rate" } : f));
  }

  // SALES-029: day-count suggestions + auto hours for the shared Days/Hours field.
  const days24 = hourPeriods(start, end);
  const daysCal = inclusiveDays(start, end);
  const winHours = Math.round(((Date.parse(end) - Date.parse(start)) / 3600000) * 10) / 10;
  const autoHours = billableServiceHours(winHours, form, formType);
  const showHalfDay = winHours > 0 && winHours < 24;

  return (
    <div className="space-y-6 mt-2">
      {/* Insurance-managed booking: the case owns the money, this form is derived. */}
      {form.insurance_case_id && (
        <div className="border border-amber-500/30 bg-amber-500/10 rounded-md px-4 py-3 text-sm" data-testid="booking-insurance-managed-banner">
          This booking is managed by insurance case{" "}
          <Link to={`/insurance/${form.insurance_case_id}`} className="text-primary hover:underline font-medium" data-testid="booking-insurance-case-link">
            {form.insurance_case_number || form.insurance_case_id}
          </Link>
          . Financial fields are set from the case — edit them there.
        </div>
      )}
      {/* Client Credentials section — rental only. Chauffeur/Tour collect client
          name/email/phone inline in the main booking section. */}
      {formType === "rental" && (
      <div className="border border-border bg-secondary/20 p-5">
        <div className="label-mono text-primary mb-4">Client Credentials</div>
        <div className="grid grid-cols-2 gap-4">
          {CLIENT_CREDENTIAL_FIELDS.map((f) => (
            f.name === "customer_name"
              ? <ClientNameFields key={f.name} form={form} setForm={setForm} />
              : <div key={f.name} className="col-span-2 md:col-span-1">
                  <Label className="label-mono mb-1.5 block">{f.label}{f.required && <span className="text-primary"> *</span>}</Label>
                  <Input
                    type="text"
                    value={form[f.name] ?? ""}
                    onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                    className="bg-input border-border"
                    data-testid={`bookings-field-${f.name}`}
                  />
                </div>
          ))}

          {/* Insurance Coverage — Yes/No bubble + conditional fields */}
          <div className="col-span-2">
            <BubbleGroup
              label="Insurance Coverage"
              required
              value={form.insurance_coverage}
              onChange={(v) => setForm({ ...form, insurance_coverage: v })}
              options={YES_NO_OPTIONS}
              testid="bubble-insurance_coverage"
            />
          </div>

          {form.insurance_coverage === "yes" && (
            <div className="col-span-2 grid grid-cols-2 gap-4 border-l-2 border-primary/40 pl-4">
              <div className="col-span-2 md:col-span-1">
                <BubbleGroup
                  label="Insurance Called"
                  value={form.insurance_called}
                  onChange={(v) => setForm({ ...form, insurance_called: v })}
                  options={YES_NO_OPTIONS}
                  testid="bubble-insurance_called"
                />
              </div>
              <div className="col-span-2 md:col-span-1">
                <BubbleGroup
                  label="Insured"
                  value={form.insured_type}
                  onChange={(v) => setForm({ ...form, insured_type: v })}
                  options={INSURED_TYPE_OPTIONS}
                  testid="bubble-insured_type"
                />
              </div>
              <div className="col-span-2 md:col-span-1">
                <BubbleGroup
                  label="Insurance Type"
                  value={form.insurance_policy_type}
                  onChange={(v) => setForm({ ...form, insurance_policy_type: v })}
                  options={INSURANCE_POLICY_OPTIONS}
                  testid="bubble-insurance_policy_type"
                />
              </div>
              <div className="col-span-2">
                <Label className="label-mono mb-1.5 block">Insurance Notes</Label>
                <Textarea
                  rows={3}
                  value={form.insurance_notes ?? ""}
                  onChange={(e) => setForm({ ...form, insurance_notes: e.target.value })}
                  className="bg-input border-border"
                  data-testid="bookings-field-insurance_notes"
                />
              </div>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Booking details */}
      <div className="grid grid-cols-2 gap-4">
      {fields.filter((f) => !f.showIf || f.showIf(form)).map((f) => (
        f.name === "customer_name" ? (
          <ClientNameFields key={f.name} form={form} setForm={setForm} />
        ) : (
        <div key={f.name} className={f.span === 2 ? "col-span-2" : "col-span-2 md:col-span-1"}>
          <Label className="label-mono mb-1.5 block">{f.label}{f.required && <span className="text-primary"> *</span>}</Label>
          {f.type === "tour-package" ? (
            <PackagePicker form={form} setForm={setForm} />
          ) : f.type === "custom-addons" ? (
            <CustomAddons value={form.custom_addons} onChange={(v) => setForm({ ...form, custom_addons: v })} />
          ) : f.type === "salesperson-select" ? (
            salespersonReadOnly ? (
              // SALES-024: a rep does not reassign ownership. The server ignores
              // any salesperson a rep's PUT carries, so it is shown as read-only
              // text rather than an editable control it cannot actually change.
              <div
                className="bg-input border border-border rounded-md h-10 px-3 flex items-center text-sm text-muted-foreground"
                data-testid={`bookings-field-${f.name}-readonly`}
              >
                {form.salesperson || "—"}
              </div>
            ) : (
            <div className="space-y-2">
              <Select
                value={showSalesOther ? "__other__" : (form.salesperson || "")}
                onValueChange={(val) => {
                  if (val === "__other__") {
                    setSalesOtherOpen(true);
                    if (salesSet.has(form.salesperson)) setForm({ ...form, salesperson: "" });
                  } else {
                    setSalesOtherOpen(false);
                    setForm({ ...form, salesperson: val });
                  }
                }}
              >
                <SelectTrigger className="bg-input border-border" data-testid={`bookings-field-${f.name}`}>
                  <SelectValue placeholder="Select salesperson…" />
                </SelectTrigger>
                <SelectContent>
                  {salesOptions.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                  <SelectItem value="__other__">Other…</SelectItem>
                </SelectContent>
              </Select>
              {showSalesOther && (
                <Input
                  value={form.salesperson || ""}
                  onChange={(e) => setForm({ ...form, salesperson: e.target.value })}
                  placeholder="Enter salesperson name"
                  className="bg-input border-border"
                  data-testid="bookings-field-salesperson-other"
                />
              )}
              {String(form.salesperson ?? "").trim().toLowerCase() === "affiliate" && (
                <div>
                  <Label className="label-mono mb-1.5 block">Which affiliate?<span className="text-primary"> *</span></Label>
                  <AffiliatePicker value={form.affiliate_name} onChange={(v) => setForm({ ...form, affiliate_name: v })} />
                </div>
              )}
            </div>
            )
          ) : f.type === "vehicle-select" ? (
            <>
            <Select value={form.vehicle_id || ""} onValueChange={onVehicleChange} disabled={!datesReady}>
              <SelectTrigger className="bg-input border-border disabled:opacity-50 disabled:cursor-not-allowed" data-testid={`bookings-field-${f.name}`}>
                <SelectValue placeholder={datesReady ? "Select vehicle…" : "Select start & end times first…"} />
              </SelectTrigger>
              <SelectContent>
                {availableVehicles(f.service).map((v) => {
                  const booked = isBooked(v.id);
                  return (
                    <SelectItem key={v.id} value={v.id} data-testid={`vehicle-option-${v.id}`}>
                      {v.make} {v.model} {v.year ? `· ${v.year}` : ""} {v.license_plate ? `· ${v.license_plate}` : ""}{booked ? "  (booked)" : ""}
                    </SelectItem>
                  );
                })}
                {availableVehicles(f.service).length === 0 && <SelectItem value="__none__" disabled>No vehicles for this service</SelectItem>}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1.5">
              {!datesReady
                ? "Set start & end times to choose a vehicle."
                : checkingAvail
                  ? "Checking availability…"
                  : "Cars marked (booked) are already reserved for this period but can still be selected."}
            </p>
            </>
          ) : f.type === "chauffeur-select" ? (
            <Select value={form.driver_id || ""} onValueChange={onDriverChange}>
              <SelectTrigger className="bg-input border-border" data-testid={`bookings-field-${f.name}`}>
                <SelectValue placeholder="Select driver…" />
              </SelectTrigger>
              <SelectContent>
                {chauffeurs.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : f.type === "select" ? (
            <Select value={form[f.name] || ""} onValueChange={(v) => setForm({ ...form, [f.name]: v })}>
              <SelectTrigger className="bg-input border-border" data-testid={`bookings-field-${f.name}`}><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>
                {f.options.map((opt) => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : f.type === "checkbox" ? (
            <div className="flex items-center gap-2 h-10">
              <Switch checked={!!form[f.name]} onCheckedChange={(v) => setForm({ ...form, [f.name]: v })} data-testid={`bookings-field-${f.name}`} />
              <span className="text-sm text-muted-foreground">{form[f.name] ? "Yes" : "No"}</span>
            </div>
          ) : f.type === "textarea" ? (
            <Textarea rows={3} value={form[f.name] ?? ""} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} className="bg-input border-border" data-testid={`bookings-field-${f.name}`} />
          ) : f.type === "datetime" ? (
            <DateTime5MinInput
              value={form[f.name] ?? ""}
              onChange={(val) => setForm({
                ...form,
                [f.name]: val,
                ...(f.name === "service_hours" ? { service_hours_manual: val !== "" } : {}),
                ...(f.name === "total" ? { total_manual: val !== "" } : {}),
              })}
              testid={`bookings-field-${f.name}`}
            />
          ) : f.name === "rental_days" ? (
            <>
              <DayHoursField
                testid="bookings-field-rental_days"
                value={form.rental_days}
                manual={!!form.rental_days_manual}
                autoValue={days24}
                step="0.5" min="0.5"
                onChange={(v, manual) => setForm({ ...form, rental_days: v, rental_days_manual: manual })}
                chips={[
                  { key: "24h", label: `24 hours: ${days24}`, value: days24, manual: false, active: !form.rental_days_manual },
                  { key: "calendar", label: `Calendar days: ${daysCal}`, value: daysCal, manual: true, active: !!form.rental_days_manual && Number(form.rental_days) === daysCal },
                  ...(showHalfDay ? [{ key: "half", label: "Half day", value: 0.5, manual: true, active: !!form.rental_days_manual && Number(form.rental_days) === 0.5 }] : []),
                ]}
              />
              <p className="text-xs text-muted-foreground mt-1.5" data-testid="rental-days-hint">
                {form.rental_days_manual
                  ? "Your number — tap a suggestion or clear the box to go back to automatic."
                  : "Suggested from the dates (24-hour days). Tap a chip or type any number (steps of 0.5)."}
              </p>
            </>
          ) : f.name === "service_hours" ? (
            <>
              <DayHoursField
                testid="bookings-field-service_hours"
                value={form.service_hours}
                manual={!!form.service_hours_manual}
                autoValue={autoHours}
                step="0.1" min="0"
                onChange={(v, manual) => setForm({ ...form, service_hours: v, service_hours_manual: manual })}
                useCalculatedLabel={form.service_hours_manual ? `Use calculated: ${autoHours} hours` : undefined}
              />
              {!form.service_hours_manual && (
                <p className="text-xs text-muted-foreground mt-1.5" data-testid="service-hours-hint">
                  {formType === "chauffeur" && Number(form.standby_hours) > 0
                    ? `Billable hours — ${Number(form.standby_hours)}h of standby has been taken out and bills at the standby rate.`
                    : "Auto-calculated from the start/end window."}
                </p>
              )}
            </>
          ) : (
            <>
            <Input
              readOnly={f.readOnly}
              type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
              value={form[f.name] ?? ""}
              onChange={(e) => {
                const val = e.target.value;
                setForm({
                  ...form,
                  [f.name]: val,
                  ...(f.name === "service_hours" ? { service_hours_manual: val !== "" } : {}),
                  ...(f.name === "rental_days" ? { rental_days_manual: val !== "" } : {}),
                  ...(f.name === "total" ? { total_manual: val !== "" } : {}),
                });
              }}
              className={`bg-input border-border ${f.readOnly ? "text-muted-foreground" : ""}`}
              data-testid={`bookings-field-${f.name}`}
            />
            {f.name === "service_hours" && (
              <p className="text-xs text-muted-foreground mt-1.5" data-testid="service-hours-hint">
                {form.service_hours_manual
                  ? "Manual override — clear the field to re-enable auto."
                  : formType === "chauffeur" && Number(form.standby_hours) > 0
                    ? `Billable hours — ${Number(form.standby_hours)}h of standby has been taken out and bills at the standby rate.`
                    : "Auto-calculated from the start/end window."}
              </p>
            )}
            {f.name === "rental_days" && (
              <p className="text-xs text-muted-foreground mt-1.5" data-testid="rental-days-hint">
                {form.rental_days_manual
                  ? "Manual override — clear the field to re-derive from the dates."
                  : "Inclusive calendar days from the start/end dates (same-day = 1)."}
              </p>
            )}
            {f.name === "total" && (
              <p className="text-xs text-muted-foreground mt-1.5" data-testid="total-hint">
                {formType !== "rental" && !chargesHourly(form)
                  ? "Whole-job pricing — type the price; nothing is added on top."
                  : formType === "rental" && legacyRentalRef.current
                    ? "This total was set before day-based pricing — type to change it; it is never recalculated."
                    : form.total_manual
                      ? "Manual override — clear the field to re-enable auto (Base + Delivery + Other Charges)."
                      : "Auto = " + (formType === "rental"
                          ? "Days × Daily rate"
                          : hasPackage(form, formType) ? "Package" : "Hours × Rate")
                        + (formType === "chauffeur" ? " + Standby" : "")
                        + " + Delivery + Other Charges. Type to override."}
              </p>
            )}
            {f.name === "standby_hours" && !chargesHourly(form) && (
              <p className="text-xs text-muted-foreground mt-1.5" data-testid="standby-hint">
                Recorded only — whole-job pricing doesn't add standby to the total.
              </p>
            )}
            {f.name === "remaining_balance" && (
              <p className="text-xs text-muted-foreground mt-1.5" data-testid="remaining-balance-hint">
                Total − deposit, kept in step automatically.
              </p>
            )}
            </>
          )}
        </div>
        )
      ))}
      {(formType === "chauffeur" || formType === "tour") && (() => {
        const num = (n) => Number(n || 0);
        const v = vehicles.find((x) => x.id === form.vehicle_id);
        const total = num(form.total);
        const fmt = (n) => `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        const isThird = v && v.ownership === "third_party";
        let ownerGets = 0, youKeep = 0, note = "", ownerName = (v && v.owner_name) || "Owner";
        if (!isThird) {
          youKeep = total - (num(form.driver_cost) + num(form.gas_cost) + num(form.other_costs));
          note = "Company-owned — you keep the net after costs.";
        } else if ((v.owner_split_type || "percent") === "base_rate") {
          ownerGets = num(form.owner_base_rate);
          youKeep = total - ownerGets;
          note = "Base-rate car — owner takes the base and covers driver + gas.";
        } else {
          const drove = !!form.owner_drove;
          const net = total - (num(form.driver_cost) + num(form.gas_cost) + num(form.other_costs));
          const base = (v.owner_split_type || "percent") === "percent_total" ? total : net;
          const pctShare = base * (num(v.owner_split_percent) / 100);
          ownerGets = pctShare + (drove ? num(form.driver_cost) : 0);
          youKeep = net - pctShare;
          note = `${num(v.owner_split_percent)}% split of ${(v.owner_split_type || "percent") === "percent_total" ? "total (gross)" : "net profit"}${drove ? " · owner drove, so their driver cost is included" : ""}.`;
        }
        const held = (form.balance_held_by || "company") === "driver";
        const dir = isThird ? (held ? `${ownerName} holds the money → they owe you ${fmt(youKeep)}` : `You hold the money → you owe ${ownerName} ${fmt(ownerGets)}`) : "";
        return (
          <div className="col-span-2 border border-primary/40 bg-primary/5 p-4" data-testid="who-gets-what">
            <div className="label-mono mb-2">Who gets what</div>
            <div className="grid grid-cols-2 gap-3">
              {isThird && (
                <div>
                  <div className="text-xs text-muted-foreground">{ownerName} gets</div>
                  <div className="font-heading text-xl text-amber-500" data-testid="wgw-owner">{fmt(ownerGets)}</div>
                </div>
              )}
              <div>
                <div className="text-xs text-muted-foreground">You keep</div>
                <div className="font-heading text-xl text-emerald-500" data-testid="wgw-company">{fmt(youKeep)}</div>
              </div>
            </div>
            <div className="text-xs text-muted-foreground mt-2">{note}{dir ? ` · ${dir}` : ""}</div>
          </div>
        );
      })()}
      <div className="col-span-2">
        <Label className="label-mono mb-1.5 block">Status</Label>
        <Select value={form.status || "pre_booking"} onValueChange={(v) => setForm({ ...form, status: v })}>
          <SelectTrigger className="bg-input border-border" data-testid="bookings-field-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="col-span-2 border-t border-border pt-4 mt-1">
        <div className="flex items-center justify-between">
          <div>
            <div className="label-mono">Add Tax (HST {Math.round(TAX_RATE * 100)}%)</div>
            <div className="text-xs text-muted-foreground">Toggle on to add tax to the total.</div>
          </div>
          <button type="button" onClick={() => setForm({ ...form, tax_applied: !form.tax_applied })} data-testid="booking-tax-toggle"
            className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors ${form.tax_applied ? "bg-primary" : "bg-secondary border border-border"}`}>
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${form.tax_applied ? "translate-x-8" : "translate-x-1"}`} />
          </button>
        </div>
        {form.tax_applied && (() => {
          const sub = Number(form.total) || 0;
          const tax = sub * TAX_RATE;
          const fmt = (n) => `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
          return (
            <div className="mt-3 border border-primary/40 bg-primary/5 p-4" data-testid="booking-tax-box">
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">Subtotal</span><span>{fmt(sub)}</span></div>
              <div className="flex justify-between text-sm mt-1"><span className="text-muted-foreground">Tax (HST {Math.round(TAX_RATE * 100)}%)</span><span>{fmt(tax)}</span></div>
              <div className="flex justify-between font-heading text-lg mt-2 pt-2 border-t border-border"><span>Total + Tax</span><span data-testid="booking-total-with-tax">{fmt(sub + tax)}</span></div>
            </div>
          );
        })()}
      </div>
      </div>
    </div>
  );
}
